import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  getUniboxAutomation,
  setUniboxAutomation,
  CommentToDmRule,
} from "../../../database/community/unibox";

// Comment-to-DM automations editor. New inbound Instagram/Facebook comments
// whose text contains a rule's keyword get an automatic private reply (and an
// optional public reply). The Meta webhook evaluates these on ingest; this
// panel is the dashboard editor for the same rules the MCP tools manage.
// Kept behind a toggle in the Unibox tab (progressive disclosure).

const Panel = styled.div`
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  padding: var(--space-3);
  margin-bottom: var(--space-3);
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr 1fr auto auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.input`
  font-family: var(--font-sans);
  font-size: 12px;
  padding: 6px 9px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  width: 100%;
`;

const Label = styled.div`
  font-family: var(--font-sans);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--color-text-secondary);
  margin-bottom: 6px;
`;

const Toggle = styled.button<{ $on: boolean }>`
  appearance: none;
  border: 1px solid ${(p) => (p.$on ? "var(--color-accent)" : "var(--color-border-subtle)")};
  background: ${(p) => (p.$on ? "var(--color-accent)" : "transparent")};
  color: ${(p) => (p.$on ? "#fff" : "var(--color-text-secondary)")};
  font-family: var(--font-sans);
  font-size: 10px;
  padding: 5px 10px;
  border-radius: var(--radius-pill);
  cursor: pointer;
  white-space: nowrap;
`;

const IconButton = styled.button`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: transparent;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: 12px;
  padding: 5px 9px;
  border-radius: var(--radius-sm);
  cursor: pointer;

  &:hover {
    color: var(--color-danger);
    border-color: var(--color-danger);
  }
`;

const Button = styled.button`
  appearance: none;
  border: 1px solid var(--color-accent);
  background: var(--color-accent);
  color: #fff;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  padding: 6px 14px;
  border-radius: var(--radius-pill);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: transparent;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: 12px;
  padding: 6px 14px;
  border-radius: var(--radius-pill);
  cursor: pointer;

  &:hover {
    color: var(--color-text);
  }
`;

const Muted = styled.div`
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--color-text-secondary);
  padding: var(--space-2) 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
`;

const emptyRule = (): CommentToDmRule => ({
  keyword: "",
  dmText: "",
  publicReply: "",
  enabled: true,
});

export const UniboxAutomations: React.FC<{ communityId: string }> = ({ communityId }) => {
  const [rules, setRules] = useState<CommentToDmRule[]>([]);
  const [snippets, setSnippets] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError("");
    try {
      const res = await getUniboxAutomation({ communityId });
      setRules(res.commentToDmRules || []);
      setSnippets(res.cannedReplies || []);
    } catch (e: any) {
      setError(e?.message || "Could not load automations.");
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    load();
  }, [load]);

  const patchRule = (i: number, patch: Partial<CommentToDmRule>) =>
    setRules((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const removeRule = (i: number) =>
    setRules((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    setStatus("");
    setError("");
    try {
      const cleaned = rules
        .map((r) => ({
          keyword: r.keyword.trim(),
          dmText: (r.dmText || "").trim(),
          publicReply: r.publicReply?.trim() || undefined,
          enabled: r.enabled !== false,
        }))
        // A rule needs a keyword and at least one action. Public-reply-only
        // rules are valid (X mentions have no auto-DM; IG/FB can go public-only).
        .filter((r) => r.keyword && (r.dmText || r.publicReply));
      const cleanedSnippets = snippets.map((s) => s.trim()).filter(Boolean);
      const res = await setUniboxAutomation({
        communityId,
        commentToDmRules: cleaned,
        cannedReplies: cleanedSnippets,
      });
      setRules(res.commentToDmRules || []);
      setSnippets(res.cannedReplies || []);
      setStatus("Saved.");
    } catch (e: any) {
      setError(e?.message || "Could not save automations.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel>
      <Label>Comment-to-DM rules</Label>
      <Muted>
        When a new Instagram or Facebook comment contains a keyword, auto-send the comment author a
        private DM (and an optional public reply). One auto-reply per comment.
      </Muted>

      {error ? <Muted style={{ color: "var(--color-danger)" }}>{error}</Muted> : null}

      {loading ? (
        <Muted>Loading…</Muted>
      ) : (
        <>
          {rules.length === 0 ? (
            <Muted>No rules yet. Add one below.</Muted>
          ) : (
            rules.map((r, i) => (
              <Row key={i}>
                <Field
                  value={r.keyword}
                  onChange={(e) => patchRule(i, { keyword: e.target.value })}
                  placeholder="keyword"
                />
                <Field
                  value={r.dmText || ""}
                  onChange={(e) => patchRule(i, { dmText: e.target.value })}
                  placeholder="Private DM (IG/FB, optional)"
                />
                <Field
                  value={r.publicReply || ""}
                  onChange={(e) => patchRule(i, { publicReply: e.target.value })}
                  placeholder="Public reply (optional)"
                />
                <Toggle
                  type="button"
                  $on={r.enabled !== false}
                  onClick={() => patchRule(i, { enabled: r.enabled === false })}
                >
                  {r.enabled !== false ? "on" : "off"}
                </Toggle>
                <IconButton type="button" title="Remove rule" onClick={() => removeRule(i)}>
                  ✕
                </IconButton>
              </Row>
            ))
          )}

          <div style={{ marginTop: "var(--space-4)" }}>
            <Label>Saved snippets (canned replies)</Label>
            <Muted>Quick-insert reusable replies from the composer. One per line.</Muted>
            {snippets.map((s, i) => (
              <Row key={i} style={{ gridTemplateColumns: "1fr auto" }}>
                <Field
                  value={s}
                  onChange={(e) =>
                    setSnippets((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))
                  }
                  placeholder="Thanks for reaching out! …"
                />
                <IconButton
                  type="button"
                  title="Remove snippet"
                  onClick={() => setSnippets((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  ✕
                </IconButton>
              </Row>
            ))}
            <GhostButton type="button" onClick={() => setSnippets((prev) => [...prev, ""])}>
              + Add snippet
            </GhostButton>
          </div>

          <Actions>
            <GhostButton type="button" onClick={() => setRules((prev) => [...prev, emptyRule()])}>
              + Add rule
            </GhostButton>
            <Button type="button" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save automations"}
            </Button>
            {status ? <Muted style={{ color: "var(--color-accent)" }}>{status}</Muted> : null}
          </Actions>
        </>
      )}
    </Panel>
  );
};

export default UniboxAutomations;
